import { useTranslation } from 'react-i18next';
import { PanelRightClose, Sparkles, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';
import { ChecklistStepCard } from './ChecklistStepCard';
import { useChecklistProgress } from '../hooks/use-checklist-progress';
import { useChecklistStore } from '../store/checklist-store';

interface OnboardingChecklistProps {
  onCollapse?: () => void;
  onDismiss?: () => void;
}

/**
 * Setup checklist rail (expanded). Mounted in the right column of AppLayout.
 * Per docs/design/onboarding-checklist.md § Rail anatomy (expanded).
 */
export function OnboardingChecklist({ onCollapse, onDismiss }: OnboardingChecklistProps) {
  const { t } = useTranslation('onboarding');
  const { steps, completedCount, total, allComplete, isLoading } = useChecklistProgress();
  const archive = useChecklistStore((s) => s.archive);

  if (isLoading) return null;

  if (allComplete) {
    return (
      <aside
        role="complementary"
        aria-labelledby="setup-checklist-title"
        className="flex h-full w-[320px] shrink-0 flex-col border-l border-border bg-card"
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3.5">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/22 bg-emerald-500/[0.08] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-500">
            <Sparkles className="h-3 w-3" />
            {t('checklist.complete.title')}
          </span>
          {onCollapse && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCollapse}>
              <PanelRightClose className="h-3.5 w-3.5" />
            </Button>
          )}
        </header>
        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          <p className="px-2 pt-2 text-sm text-muted-foreground">{t('checklist.complete.body')}</p>
          <Button
            onClick={archive}
            className="w-full bg-amber-500 text-[#0a0b10] hover:bg-amber-400"
          >
            {t('checklist.complete.archive')}
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      role="complementary"
      aria-labelledby="setup-checklist-title"
      className="flex h-full w-[320px] shrink-0 flex-col border-l border-border bg-card"
    >
      <header className="space-y-2 border-b border-border px-4 py-3.5">
        <div className="flex items-center justify-between">
          <span
            id="setup-checklist-title"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/[0.08] px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-500"
          >
            <Sparkles className="h-3 w-3" />
            {t('checklist.title')}
          </span>
          <div className="flex items-center gap-0.5">
            {onCollapse && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onCollapse}
                aria-label={t('checklist.collapse')}
              >
                <PanelRightClose className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDismiss && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onDismiss}
                aria-label={t('checklist.dismiss')}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs text-foreground">
            {t('checklist.progress', { completed: completedCount, total })}
          </p>
          <div className="h-1 overflow-hidden rounded-full bg-white/[0.04]">
            <div
              className="h-full rounded-full bg-amber-500 transition-all"
              style={{ width: `${(completedCount / total) * 100}%` }}
            />
          </div>
        </div>
      </header>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {steps.map((step, idx) => (
          <ChecklistStepCard key={step.id} step={step} stepNumber={idx + 1} />
        ))}
      </div>
      <footer className="border-t border-border px-4 py-3">
        <a
          href="mailto:founders@loki-trader.com"
          className={cn(
            'block font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground',
          )}
        >
          {t('checklist.foot')}
        </a>
      </footer>
    </aside>
  );
}
